/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Text } from '@/shared/ui';

/**
 * Shared overlay for the ad dialogs: a scrim-backed, centered card that
 * closes on Escape or a scrim click. Rendered into `document.body` so it
 * stacks above any dialog it is opened from (the publish dialog).
 */
export function AdDialogShell({
  titleId,
  title,
  onDismiss,
  testId,
  children,
}: {
  titleId: string;
  title: string;
  onDismiss: () => void;
  testId: string;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onDismiss]);

  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center"
      onClick={(event) => { event.stopPropagation(); onDismiss(); }}
      data-testid={testId}
    >
      <div className="absolute inset-0 bg-[var(--overlay-scrim)]" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex flex-col w-full max-w-md mx-(--space-4) p-(--space-5) gap-(--space-4) rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Spacing is a flex `gap`, not `space-y`: every `Text` child carries
            `m-0`, and `m-0` beats the zero-specificity `space-y` selector, so the
            blocks used to sit flush against each other. The title is the same
            style as DialogTitle so these read like every other Housien dialog. */}
        <Text
          id={titleId}
          className="m-0 text-[length:var(--text-app-section)] [font-weight:var(--font-semibold)] leading-[var(--leading-tight)] tracking-[var(--tracking-tight)] text-[var(--text-primary)]"
        >
          {title}
        </Text>
        {children}
      </div>
    </div>,
    document.body,
  );
}
